import dayjs from 'dayjs'

type NativeWebView = {
  postMessage?: (_data: string) => void
}

type WidgetPrayerTime = {
  Imsak: string | null
  Gunes: string | null
  Ogle: string | null
  Ikindi: string | null
  Aksam: string | null
  Yatsi: string | null
}

type WidgetPrayerDay = {
  date: string
  times: WidgetPrayerTime
}

type WidgetStatsDay = {
  date: string
  score: number
}

export type NativeWidgetSnapshot = {
  version: 1
  updatedAt: string
  prayer?: {
    locationName?: string
    districtId?: string
    days: WidgetPrayerDay[]
  }
  stats?: {
    todayScore: number
    streak: number
    activeDays: number
    totalScore: number
    lastSevenDays: WidgetStatsDay[]
  }
}

type NativeWidgetPartial = Omit<Partial<NativeWidgetSnapshot>, 'version' | 'updatedAt'>

type DailyScoreMap = Record<string, { score?: number }>

const STORAGE_KEY = 'hayirhah-native-widget-state'

/** Returns the React Native WebView bridge when the site runs inside the mobile app. */
const getWebView = () => {
  if (typeof window === 'undefined') return null

  return (window as unknown as { ReactNativeWebView?: NativeWebView }).ReactNativeWebView
}

/** Reads the last widget snapshot so partial updates (prayer or stats) can be merged. */
const readCurrentSnapshot = (): NativeWidgetSnapshot => {
  const fallback: NativeWidgetSnapshot = {
    version: 1,
    updatedAt: new Date().toISOString(),
  }

  if (typeof window === 'undefined') return fallback

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? { ...fallback, ...JSON.parse(stored) } : fallback
  } catch {
    return fallback
  }
}

/** Persists the merged widget snapshot for the next partial update. */
const writeCurrentSnapshot = (snapshot: NativeWidgetSnapshot) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
  } catch {
    // Widget sync should never block the app UI.
  }
}

/** Merges a partial widget payload and sends it to the Android home screen widget. */
export const syncNativeWidgetData = (partial: NativeWidgetPartial) => {
  const webView = getWebView()
  if (!webView?.postMessage || typeof window === 'undefined') return

  const snapshot: NativeWidgetSnapshot = {
    ...readCurrentSnapshot(),
    ...partial,
    version: 1,
    updatedAt: new Date().toISOString(),
  }

  writeCurrentSnapshot(snapshot)
  webView.postMessage(JSON.stringify({ type: 'syncWidgetData', payload: snapshot }))
}

/** Builds the Hayır Zinciri summary (today, streak, last seven days) shown on the widget. */
export const buildHayirZinciriWidgetStats = (dailyStats: DailyScoreMap) => {
  const today = dayjs()
  const todayKey = today.format('DD-MM-YYYY')
  const values = Object.values(dailyStats)
  const activeDays = values.filter((stat) => (stat.score || 0) > 0).length
  const totalScore = values.reduce((sum, stat) => sum + (stat.score || 0), 0)
  const lastSevenDays = Array.from({ length: 7 }, (_, index) => {
    const date = today.subtract(6 - index, 'day')
    const key = date.format('DD-MM-YYYY')
    return {
      date: date.format('YYYY-MM-DD'),
      score: dailyStats[key]?.score || 0,
    }
  })

  return {
    todayScore: dailyStats[todayKey]?.score || 0,
    streak: getCurrentStreak(dailyStats),
    activeDays,
    totalScore,
    lastSevenDays,
  }
}

/** Counts consecutive active days back from today, tolerating a single one-day gap. */
const getCurrentStreak = (dailyStats: DailyScoreMap) => {
  let streak = 0
  let currentDay = dayjs()
  let skippedGap = false

  while (true) {
    const key = currentDay.format('DD-MM-YYYY')
    const score = dailyStats[key]?.score || 0

    if (score > 0) {
      streak += 1
      skippedGap = false
      currentDay = currentDay.subtract(1, 'day')
      continue
    }

    if (!skippedGap && streak > 0) {
      skippedGap = true
      currentDay = currentDay.subtract(1, 'day')
      continue
    }

    return streak
  }
}
